<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { getDeviceSettings, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { devices } from 'src/stores/lists/devices';
	import { real_id } from 'src/helpers/interface_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import { bindStickyBottomAction } from 'src/helpers/sticky_bottom_action';
	import { format } from 'svelte-i18n';
	import { Button, Container } from '@Shelly/shelly-design-system';
	import SettingGuide from '../../../SettingGuide.svelte';
	import InputTypeWallDisplay from './InputTypeWallDisplay.svelte';
	import OutputTypeG2 from './OutputTypeG2.svelte';
	import ActionPowerOnG2 from '../ActionPowerOnG2.svelte';
	import InvertInputByIdG2 from './InvertInputByIdG2.svelte';
	import PhysicalInputContainer from './PhysicalInputContainer.svelte';
	import InputState from '../../../ApplicationSettings/InputState.svelte';
	import { deviceSettingsAdvanced } from 'src/helpers/settings/device_settings_advanced';
	import {
		buildIoFieldConfig,
		deviceHasInputStateSetting,
		deviceShowsInputStateSetting,
		isNonEmptyConfig,
		sendIoSavePayloads,
	} from './io_save';
	import AdvancedSettingBlock from './AdvancedSettingBlock.svelte';

	export let device;

	const id = real_id(device.id);
	const channel = device.channel ?? 0;

	$: settingsStore = getDeviceSettings(id);
	let settings = readDeviceSettings(id);
	$: settings = $settingsStore ?? settings;

	let inputType = settings?.[`input:${channel}`]?.type;
	let cacheInputType = inputType;
	let outputType = settings?.[`switch:0`]?.in_mode;
	let cacheOutputType = outputType;
	let initialStateValue = settings?.[`switch:0`]?.initial_state;
	let cacheInitialState = initialStateValue;
	let invertValue = settings?.[`input:${channel}`]?.invert ?? false;
	let cacheInvert = invertValue;
	let inputStateValue = $devices[device.id]?.input_state || false;
	let cacheInputState = inputStateValue;
	let loading = false;

	$: showInvert = settings?.[`input:${channel}`]?.invert !== undefined;
	$: hasInputStateSetting = deviceHasInputStateSetting(device);
	$: showInputState = deviceShowsInputStateSetting(device, $deviceSettingsAdvanced);
	$: showPhysicalInput = $showNewLayout && (showInvert || showInputState);

	const saveAll = async () => {
		loading = true;
		const inputConfig = buildIoFieldConfig(
			{
				type: inputType,
				...(showInvert ? { invert: invertValue } : {}),
			},
			{
				type: cacheInputType,
				invert: cacheInvert,
			},
		);
		const controlNext: { in_mode?: string; initial_state?: string } = {};
		if (outputType !== cacheOutputType) {
			controlNext.in_mode = outputType;
			if (initialStateValue === cacheInitialState) {
				controlNext.initial_state = outputType !== 'follow' ? 'restore_last' : 'off';
			}
		}
		if (initialStateValue !== cacheInitialState) {
			controlNext.initial_state = initialStateValue;
		}
		const controlConfig = buildIoFieldConfig(controlNext, {
			in_mode: cacheOutputType,
			initial_state: cacheInitialState,
		});

		const { ok } = await sendIoSavePayloads({
			device,
			inputUpdates: isNonEmptyConfig(inputConfig)
				? [{ id: channel, config: inputConfig }]
				: [],
			controlUpdates: isNonEmptyConfig(controlConfig)
				? [{ id: 0, method: 'Switch.SetConfig', config: controlConfig }]
				: [],
			inputState: showInputState ? inputStateValue : undefined,
			cachedInputState: cacheInputState,
		});

		if (ok) {
			if (inputConfig.type !== undefined) {
				settings[`input:${channel}`].type = inputType;
				cacheInputType = inputType;
			}
			if (inputConfig.invert !== undefined) {
				settings[`input:${channel}`].invert = invertValue;
				cacheInvert = invertValue;
			}
			if (controlConfig.in_mode !== undefined) {
				settings[`switch:0`].in_mode = outputType;
				cacheOutputType = outputType;
			}
			if (controlConfig.initial_state !== undefined) {
				settings[`switch:0`].initial_state = controlConfig.initial_state;
				cacheInitialState = controlConfig.initial_state;
				initialStateValue = controlConfig.initial_state;
			}
			if (showInputState) cacheInputState = inputStateValue;
			devices_settings.update_devices_settings(id, settings);
		}
		loading = false;
	};

	$: isDirty =
		inputType !== cacheInputType ||
		outputType !== cacheOutputType ||
		initialStateValue !== cacheInitialState ||
		(showInvert && invertValue !== cacheInvert) ||
		(showInputState && inputStateValue !== cacheInputState);

	const { stickyBottomAction, setStickyBottomAction } = bindStickyBottomAction();

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			setStickyBottomAction({
				onClick: saveAll,
				loading,
				disabled: loading || !isDirty,
				icon: 'fas fa-save',
			});
		} else {
			setStickyBottomAction(null);
		}
	};
</script>

<SettingGuide
	text={$format('settings_guide_input_output', {
		default:
			'Choose how the wall switch or button drives the output — flip switch, momentary push, and what happens after a power cut.',
	})}
/>

{#if $showNewLayout}
	<Container padding="s" type="borderSolid" backgroundColor="color-base">
		<InputTypeWallDisplay {device} deferSave bind:inputType />
		<hr class="mb-m mt-s" />
		<OutputTypeG2 {device} deferSave bind:value={outputType} {inputType} />
		<hr class="mb-m mt-s" />
		<ActionPowerOnG2 {device} deferSave bind:value={initialStateValue} />
	</Container>
	<PhysicalInputContainer visible={showPhysicalInput}>
			{#if showInvert}
				<div class="mb-m">
					<InvertInputByIdG2
						{device}
						inputId={channel}
						deferSave
						bind:value={invertValue}
						title={capFirstChar(
							$format('sub_settings_title_invert_input', { default: 'Invert input' }),
						)}
					/>
				</div>
			{/if}
			{#if hasInputStateSetting}
				<AdvancedSettingBlock collapsed={!showInputState}>
					<InputState
						{device}
						deferSave
						bind:value={inputStateValue}
						title={capFirstChar(
							$format('sub_settings_title_input_state', { default: 'Input state' }),
						)}
					/>
				</AdvancedSettingBlock>
			{/if}
		</PhysicalInputContainer>
{:else}
	<InputTypeWallDisplay {device} />
	<OutputTypeG2 {device} />
	<ActionPowerOnG2 {device} />
	<Button class="mt-s" {loading} size="m" width="full" on:click={saveAll}>
		{$format('button_save', { default: 'Save' })}
	</Button>
{/if}
